class VariantButtonSwitcher {
  constructor() {
    this.init();
  }

  init() {
    // Wait for DOM to be ready
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => this.setupEventListeners());
    } else {
      this.setupEventListeners();
    }
  }

  setupEventListeners() {
    // Listen for variant changes on both radio buttons and dropdowns
    document.addEventListener('change', (e) => {
      if (e.target.matches('select.select__select') || 
          e.target.matches('input[type="radio"][name^="options"]')) {
        // Small delay to ensure variant data is updated
        setTimeout(() => this.updateButtons(), 100);
      }
    });

    // Listen for variant input changes (hidden input that stores selected variant)
    const variantObserver = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.type === 'attributes' && mutation.attributeName === 'value') {
          setTimeout(() => this.updateButtons(), 50);
        }
      });
    });

    // Observe all variant inputs for value changes
    document.querySelectorAll('input[name="id"]').forEach(input => {
      variantObserver.observe(input, { attributes: true, attributeOldValue: true });
    });

    // Watch for quick-add modal content changes
    this.watchQuickAddModals();

    // Initial update with multiple attempts to ensure it runs
    this.updateButtons();
    setTimeout(() => this.updateButtons(), 100);
    setTimeout(() => this.updateButtons(), 300);
    setTimeout(() => this.updateButtons(), 500);
  }

  watchQuickAddModals() {
    // Observe all quick-add modal content areas for new content
    const quickAddObserver = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.type === 'childList' && mutation.addedNodes.length > 0) {
          // Quick add modal content was loaded
          console.log('Quick add modal content loaded, initializing buttons');
          
          // Small delay to ensure content is fully rendered
          setTimeout(() => {
            this.updateButtons();
            
            // Re-observe new variant inputs that were just added
            const newVariantInputs = document.querySelectorAll('input[name="id"]');
            newVariantInputs.forEach(input => {
              if (!input.hasAttribute('data-observed')) {
                input.setAttribute('data-observed', 'true');
                const variantObserver = new MutationObserver((mutations) => {
                  mutations.forEach((mutation) => {
                    if (mutation.type === 'attributes' && mutation.attributeName === 'value') {
                      setTimeout(() => this.updateButtons(), 50);
                    }
                  });
                });
                variantObserver.observe(input, { attributes: true, attributeOldValue: true });
              }
            });
          }, 100);
          
          // Additional update after a bit more time
          setTimeout(() => this.updateButtons(), 300);
        }
      });
    });

    // Observe all quick-add modal content areas
    document.querySelectorAll('[id^="QuickAddInfo-"]').forEach(modalContent => {
      quickAddObserver.observe(modalContent, { childList: true, subtree: false });
    });

    // Also watch for new quick-add modals being added to the page
    const bodyObserver = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        mutation.addedNodes.forEach((node) => {
          if (node.nodeType === 1 && node.matches && node.matches('[id^="QuickAddInfo-"]')) {
            quickAddObserver.observe(node, { childList: true, subtree: false });
          }
        });
      });
    });

    bodyObserver.observe(document.body, { childList: true, subtree: true });
  }

  updateButtons() {
    // Find all product forms on the page (including in quick-add modals)
    const productForms = document.querySelectorAll('product-form');
    
    console.log('updateButtons: Found', productForms.length, 'product forms');
    
    productForms.forEach(form => {
      // Look for section (normal product page) or quick-add modal container
      const section = form.closest('section') || form.closest('.quick-add-modal__content-info') || form.closest('[id^="QuickAddInfo-"]');
      if (!section) {
        console.warn('updateButtons: No section found for form');
        return;
      }

      const variantRadios = section.querySelector('variant-radios');
      const variantSelects = section.querySelector('variant-selects');
      
      if (!variantRadios && !variantSelects) {
        // No variant picker found, try to get variant from hidden input
        const variantInput = form.querySelector('input[name="id"]');
        if (variantInput && variantInput.value) {
          this.updateButtonsByVariantId(form, variantInput.value);
        }
        return;
      }

      // Get current variant data
      let currentVariant = null;
      let variantsData = null;

      // Try to get variant data from variant-radios
      if (variantRadios) {
        const scriptTag = variantRadios.querySelector('script[type="application/json"]');
        if (scriptTag) {
          try {
            variantsData = JSON.parse(scriptTag.textContent);
            const options = this.getSelectedOptions(variantRadios);
            currentVariant = this.findVariantByOptions(variantsData, options);
          } catch (e) {
            console.error('Error parsing variant data:', e);
          }
        }
      }
      
      // Try to get variant data from variant-selects
      if (variantSelects && !currentVariant) {
        const scriptTag = variantSelects.querySelector('script[type="application/json"]');
        if (scriptTag) {
          try {
            variantsData = JSON.parse(scriptTag.textContent);
            const options = this.getSelectedOptions(variantSelects);
            currentVariant = this.findVariantByOptions(variantsData, options);
          } catch (e) {
            console.error('Error parsing variant data:', e);
          }
        }
      }

      if (!currentVariant) {
        // Fallback: try to get variant from hidden input
        const variantInput = form.querySelector('input[name="id"]');
        if (variantInput && variantInput.value && variantsData) {
          currentVariant = variantsData.find(v => v.id.toString() === variantInput.value.toString());
          console.log('updateButtons: Found variant from hidden input:', currentVariant);
        }
      }

      if (!currentVariant) {
        console.warn('updateButtons: No current variant found');
        return;
      }

      console.log('updateButtons: Current variant:', currentVariant.name || currentVariant.title, 'Available:', currentVariant.available);

      // Update buttons based on variant availability
      this.toggleButtons(form, currentVariant);
    });
  }

  updateButtonsByVariantId(form, variantId) {
    // This is a simpler fallback method
    console.log('updateButtonsByVariantId: Variant ID:', variantId);
    
    const whatsappButton = form.querySelector('.whatsapp-button');
    const buyButtonsContainer = form.querySelector('.buy-buttons-container');
    
    if (!whatsappButton || !buyButtonsContainer) {
      console.warn('updateButtonsByVariantId: Buttons not found');
      return;
    }

    // Check if the add to cart button is disabled (indicates out of stock)
    const addButton = form.querySelector('[name="add"]');
    const variantInput = form.querySelector('input[name="id"]');
    const isDisabled = addButton && addButton.hasAttribute('disabled');
    const inputDisabled = variantInput && variantInput.hasAttribute('disabled');
    const isAvailable = addButton && !isDisabled && !inputDisabled;

    console.log('updateButtonsByVariantId: Available:', isAvailable, 'Add button disabled:', isDisabled, 'Input disabled:', inputDisabled);

    if (isAvailable) {
      console.log('updateButtonsByVariantId: Showing buy buttons');
      whatsappButton.style.setProperty('display', 'none', 'important');
      buyButtonsContainer.style.setProperty('display', 'block', 'important');
    } else {
      console.log('updateButtonsByVariantId: Showing WhatsApp button');
      whatsappButton.style.setProperty('display', 'block', 'important');
      buyButtonsContainer.style.setProperty('display', 'none', 'important');
      
      // Ensure WhatsApp button is enabled
      whatsappButton.removeAttribute('disabled');
      whatsappButton.disabled = false;
    }
  }

  getSelectedOptions(variantContainer) {
    const options = [];
    
    if (variantContainer.tagName.toLowerCase() === 'variant-radios') {
      // Handle radio buttons
      const fieldsets = variantContainer.querySelectorAll('fieldset');
      fieldsets.forEach(fieldset => {
        const checkedRadio = fieldset.querySelector('input[type="radio"]:checked');
        if (checkedRadio) {
          options.push(checkedRadio.value);
        }
      });
    } else if (variantContainer.tagName.toLowerCase() === 'variant-selects') {
      // Handle dropdowns
      const selects = variantContainer.querySelectorAll('select.select__select');
      selects.forEach(select => {
        options.push(select.value);
      });
    }
    
    return options;
  }

  findVariantByOptions(variants, selectedOptions) {
    return variants.find(variant => {
      return variant.options.every((option, index) => {
        return option === selectedOptions[index];
      });
    });
  }

  toggleButtons(form, variant) {
    let whatsappButton = form.querySelector('.whatsapp-button');
    const buyButtonsContainer = form.querySelector('.buy-buttons-container');
    
    if (!whatsappButton || !buyButtonsContainer) {
      console.warn('toggleButtons: Button containers not found', {
        hasWhatsapp: !!whatsappButton,
        hasBuyButtons: !!buyButtonsContainer
      });
      return;
    }

    const isAvailable = variant.available;
    
    console.log('toggleButtons: Variant available:', isAvailable, 'Variant:', variant.name || variant.title);
    
    if (isAvailable) {
      // Show buy buttons, hide WhatsApp button
      console.log('toggleButtons: Showing buy buttons, hiding WhatsApp');
      whatsappButton.style.setProperty('display', 'none', 'important');
      buyButtonsContainer.style.setProperty('display', 'block', 'important');
      
      // Enable the add to cart button
      const addButton = form.querySelector('[name="add"]');
      if (addButton && addButton.hasAttribute('disabled')) {
        addButton.removeAttribute('disabled');
      }
    } else {
      // Show WhatsApp button, hide buy buttons
      console.log('toggleButtons: Showing WhatsApp button, hiding buy buttons');
      whatsappButton.style.setProperty('display', 'block', 'important');
      buyButtonsContainer.style.setProperty('display', 'none', 'important');
      
      // Ensure WhatsApp button is never disabled
      if (whatsappButton.hasAttribute('disabled')) {
        whatsappButton.removeAttribute('disabled');
      }
      whatsappButton.disabled = false;
      
      // Update WhatsApp button URL with current variant info
      // This will replace the button with a new one that has proper event listeners
      whatsappButton = this.updateWhatsAppUrl(form, whatsappButton, variant);
    }
  }

  updateWhatsAppUrl(form, whatsappButton, variant) {
    const productTitle = whatsappButton.getAttribute('data-product-title');
    const sectionId = whatsappButton.getAttribute('data-section-id');
    
    let variantOptions = '';
    if (variant.options && variant.options.length > 0) {
      // Filter and clean variant options
      const cleanOptions = variant.options
        .filter(option => option && option !== 'Default Title')
        .map(option => {
          // Normalize sold-out label text
          return option
            .replace(/WhatsApp to Preorder/gi, '')
            .replace(/-\s*WhatsApp to Preorder/gi, '')
            .replace(/Size-Whatsapp to Preorder/gi, 'Size-Sold Out')
            .trim();
        })
        .filter(option => option !== '');
      
      variantOptions = cleanOptions.join(', ');
    }

    let message = `Hi, I am interested in ${productTitle}`;
    if (variantOptions) {
      message += ` in size ${variantOptions}`;
    }
    const productUrl = window.location.href;
    message += `. Can you assist me with Preordering?\n\n${productUrl}`;

    const whatsappUrl = `https://wa.me/18683337401?text=${encodeURIComponent(message)}`;
    
    // Update the button's onclick and data attribute
    whatsappButton.setAttribute('data-whatsapp-url', whatsappUrl);
    whatsappButton.setAttribute('onclick', `window.open('${whatsappUrl}', '_blank', 'noopener,noreferrer')`);
    
    // Also add a direct click event listener as a fallback
    // Remove any existing listeners first
    const newButton = whatsappButton.cloneNode(true);
    whatsappButton.parentNode.replaceChild(newButton, whatsappButton);
    
    // Add the click listener to the new button
    newButton.addEventListener('click', function(e) {
      e.preventDefault();
      e.stopPropagation();
      window.open(whatsappUrl, '_blank', 'noopener,noreferrer');
    }, false);
    
    // Update the reference in the form for future calls
    return newButton;
  }
}

// Initialize the variant button switcher
new VariantButtonSwitcher();
